import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Upload, X, Image } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteAttachment } from '../../../api/src/types/onsite-types.js';

export default function PhotoCapturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');
  const [previews, setPreviews] = useState<{ file: File; url: string }[]>([]);

  const { data: attachmentsData } = useQuery({
    queryKey: ['attachments', id],
    queryFn: () => onsiteApiFetch<{ attachments: OnsiteAttachment[] }>(
      `/api/v1/onsite/attachments?job_id=${id}`
    ),
    enabled: !!id,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', id!);
      if (caption) formData.append('caption', caption);
      const base = import.meta.env.VITE_ONSITE_API_URL ?? 'https://api.theonestack.com';
      const res = await fetch(`${base}/api/v1/onsite/attachments`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (res.status === 401) {
        window.location.href = 'https://app.theonestack.com/login?redirect=onsite';
        throw new Error('Not authenticated');
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error ?? `Upload failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      onsiteApiFetch(`/api/v1/onsite/attachments/${attachmentId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', id] }),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const newPreviews = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviews(prev => [...prev, ...newPreviews]);
  }

  async function handleUploadAll() {
    for (const preview of previews) {
      await uploadMutation.mutateAsync({ file: preview.file, caption });
      URL.revokeObjectURL(preview.url);
    }
    setPreviews([]);
    setCaption('');
  }

  const existing = attachmentsData?.attachments ?? [];

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">Photos</h1>
        <button
          onClick={() => navigate(`/jobs/${id}`)}
          className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
        >
          Back to Job
        </button>
      </div>

      {/* Camera / file picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="mb-4 grid grid-cols-2 gap-3">
        <button
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.setAttribute('capture', 'environment');
              fileInputRef.current.click();
            }
          }}
          className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary/50 bg-primary/10 px-4 py-6 text-primary hover:bg-primary/20"
        >
          <Camera className="h-8 w-8" />
          <span className="text-sm font-medium">Take Photo</span>
        </button>
        <button
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.removeAttribute('capture');
              fileInputRef.current.click();
            }
          }}
          className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-secondary px-4 py-6 text-muted-foreground hover:bg-secondary"
        >
          <Upload className="h-8 w-8" />
          <span className="text-sm font-medium">Upload Files</span>
        </button>
      </div>

      {/* Pending uploads */}
      {previews.length > 0 && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4">
          <p className="mb-3 text-sm font-medium text-foreground">{previews.length} photo{previews.length !== 1 ? 's' : ''} ready to upload</p>
          <div className="mb-3 grid grid-cols-3 gap-2">
            {previews.map((p, i) => (
              <div key={i} className="relative aspect-square overflow-hidden rounded-lg">
                <img src={p.url} alt="" className="h-full w-full object-cover" />
                <button
                  onClick={() => {
                    URL.revokeObjectURL(p.url);
                    setPreviews(prev => prev.filter((_, j) => j !== i));
                  }}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-foreground hover:bg-red-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <input
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="mb-3 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <button
            onClick={handleUploadAll}
            disabled={uploadMutation.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {uploadMutation.isPending ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload All
              </>
            )}
          </button>
        </div>
      )}

      {/* Existing attachments */}
      <div className="flex-1 overflow-y-auto">
        {existing.length === 0 && previews.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
            <Image className="h-10 w-10" />
            <p>No photos yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {existing.map(a => (
              <div key={a.id} className="relative aspect-square overflow-hidden rounded-lg bg-secondary">
                <img src={a.blob_url} alt={a.caption ?? a.file_name} className="h-full w-full object-cover" />
                <button
                  onClick={() => deleteMutation.mutate(a.id)}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-600/80 text-foreground hover:bg-red-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
